import { __rest, __assign } from 'tslib';
import React, { Children } from 'react';
import { grayScale } from '@panwds/tailwindcss';
import { Portal } from '@reach/portal';
import { TooltipPopup, useTooltip } from '@reach/tooltip';
import { animated, useTransition } from '@react-spring/web';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import '@popperjs/core';

var AnimatedTooltipPopup = animated(TooltipPopup);
var appearanceStyles = {
    dark: "tw-bg-gray-900 tw-text-dark-bg",
    light: "tw-bg-gray-100 tw-text-default tw-border",
    white: "tw-bg-white tw-text-default tw-border"
};
var triangleAppearanceStyles = {
    dark: grayScale[900],
    light: grayScale[100],
    white: "white"
};
/**
 * Tooltips displays additional information in a popup when users hover over or focus on an element.
 *
 * The element should be an interactive element, like a button or link; not a div or span.
 *
 * The element is connected to the tooltip via the `aria-describedby` attribute.
 *
 * Best practices:
 * * Don’t use tooltips for information that is vital to task completion.
 * * Avoid adding interactive content to a tooltip. Use a Modal or Dialog instead.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Tooltip } from "@panwds/react-ui";
 * ```
 *
 */
function Tooltip(_a) {
    var _b = _a.appearance, appearance = _b === void 0 ? "dark" : _b, children = _a.children, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, DEBUG_STYLE = _a.DEBUG_STYLE, label = _a.label, isVisibleOverride = _a.isVisibleOverride, hasTriangle = _a.hasTriangle, passThrough = __rest(_a, ["appearance", "children", "dataMetrics", "dataTestId", "DEBUG_STYLE", "label", "isVisibleOverride", "hasTriangle"]);
    var child = Children.only(children);
    // We need to pass some properties from the child into useTooltip
    // to make sure users can maintain control over the trigger's ref and events
    // See https://github.com/reach/reach-ui/blob/353f409a296a648c4fe2c2d06b14a39b7b6dc3e1/packages/tooltip/src/index.tsx#L427
    var _c = useTooltip({
        onPointerEnter: child.props.onPointerEnter,
        onPointerMove: child.props.onPointerMove,
        onPointerLeave: child.props.onPointerLeave,
        onPointerDown: child.props.onPointerDown,
        onMouseEnter: child.props.onMouseEnter,
        onMouseMove: child.props.onMouseMove,
        onMouseLeave: child.props.onMouseLeave,
        onMouseDown: child.props.onMouseDown,
        onFocus: child.props.onFocus,
        onBlur: child.props.onBlur,
        onKeyDown: child.props.onKeyDown,
        disabled: child.props.disabled,
        DEBUG_STYLE: DEBUG_STYLE
    }), trigger = _c[0], tooltip = _c[1], isVisibleBase = _c[2];
    var isVisible = typeof isVisibleOverride === "boolean" ? isVisibleOverride : isVisibleBase;
    var transitions = useTransition(isVisible, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { mass: 1, tension: 500, friction: 40 }
    });
    // destructure off what we need to position the triangle
    var triggerRect = tooltip.triggerRect;
    var className = classNames(appearanceStyles[appearance], "tw-font-lato tw-absolute tw-text-xs tw-p-2 tw-rounded tw-shadow-md tw-z-40 tw-pointer-events-none tw-max-w-lg tw-break-words");
    return (React.createElement(React.Fragment, null,
        React.cloneElement(child, trigger),
        isVisible && hasTriangle && (
        // The Triangle. We position it relative to the trigger, not the popup
        // so that collisions don't have a triangle pointing off to nowhere.
        // Using a Portal may seem a little extreme, but we can keep the
        // positioning logic simpler here instead of needing to consider
        // the popup's position relative to the trigger and collisions
        React.createElement(Portal, null,
            React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "tooltip-triangle", className: "tw-absolute tw-w-0 tw-h-0", style: {
                    left: "".concat(triggerRect && triggerRect.left + 10, "px"),
                    top: "".concat(triggerRect && triggerRect.bottom + window.scrollY, "px"),
                    borderLeft: "10px solid transparent",
                    borderRight: "10px solid transparent",
                    borderBottom: "10px solid ".concat(triangleAppearanceStyles[appearance])
                } }))),
        transitions(function (styles, shouldRender) {
            return shouldRender && (React.createElement(AnimatedTooltipPopup, __assign({ label: label }, tooltip, passThrough, { className: className, isVisible: isVisible, style: styles })));
        })));
}

export { Tooltip };
//# sourceMappingURL=Tooltip.esm.js.map
